import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import NoticeDetailsModal from './parts/NoticeDetailsModal';
import AddNoticeModal from './parts/AddNoticeModal';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';

interface Notice {
  notice_id: string;
  institute_id: string;
  branch_id: string;
  batch_id: string | null;
  broadcast_type: string;
  sent_to: string[];
  notice_title: string;
  notice_body: string;
  files: string[];
  created_at: string;
  created_by: string;
}

interface ApiResponse {
  success: boolean;
  data: Notice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const NoticeBoard: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  const [selectedNotices, setSelectedNotices] = useState<Notice[] | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedNoticeId, setSelectedNoticeId] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);

  const { profile, loading: profileLoading } = useOperatorProfileStore();

  const limitOptions: DropdownOption[] = [
    { label: 'Show 5', value: 5 },
    { label: 'Show 10', value: 10 },
    { label: 'Show 20', value: 20 },
    { label: 'Show 50', value: 50 }
  ];

  useEffect(() => {
    if (profile?.branch_id) {
      fetchNotices();
    }
  }, [page, limit, profile]);

  const fetchNotices = async () => {
    if (!profile?.branch_id) {
      setError('Branch information not available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await apiClient.get<ApiResponse>(
        `/v1/noticeboard/by-branch/${profile.branch_id}?${params}`
      );

      if (response.data.success) {
        setNotices(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load notices');
      }
    } catch (err: any) {
      console.error('Failed to fetch notices:', err);
      setError(err.response?.data?.error || 'Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotice = async (noticeId: string) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;

    try {
      const response = await apiClient.delete(`/v1/noticeboard/${noticeId}`);
      if (response.data.success) {
        fetchNotices();
      } else {
        setError('Failed to delete notice');
      }
    } catch (err: any) {
      console.error('Failed to delete notice:', err);
      setError(err.response?.data?.error || 'Failed to delete notice');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedNotices || selectedNotices.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedNotices.length} notice(s)?`)) return;

    try {
      await Promise.all(
        selectedNotices.map(notice =>
          apiClient.delete(`/v1/noticeboard/${notice.notice_id}`)
        )
      );
      setSelectedNotices(null);
      fetchNotices();
    } catch (err: any) {
      console.error('Failed to delete notices:', err);
      setError(err.response?.data?.error || 'Failed to delete notices');
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // ── Badge colors aligned with navy/gold palette ──────────────────────────
  const broadcastTypeTemplate = (rowData: Notice) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      branch:     { bg: 'rgba(251,178,31,0.15)',  text: '#b8860b',  border: 'rgba(251,178,31,0.4)'  },
      batch:      { bg: 'rgba(14,34,93,0.1)',      text: '#0e225d',  border: 'rgba(14,34,93,0.25)'   },
      individual: { bg: 'rgba(239,68,68,0.1)',     text: '#c62828',  border: 'rgba(239,68,68,0.3)'   }
    };

    const color = colors[rowData.broadcast_type] || {
      bg: 'rgba(100,116,139,0.1)',
      text: '#475569',
      border: 'rgba(100,116,139,0.3)'
    };

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.72rem',
        fontWeight: '700',
        background: color.bg,
        color: color.text,
        border: `1px solid ${color.border}`,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'inline-block'
      }}>
        {rowData.broadcast_type}
      </span>
    );
  };

  const noticeBodyTemplate = (rowData: Notice) => {
    const maxLength = 60;
    const body = rowData.notice_body || '-';
    return (
      <div style={{
        maxWidth: '300px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: '#6b7280',
        fontSize: '0.85rem'
      }}>
        {body.length > maxLength ? `${body.substring(0, maxLength)}...` : body}
      </div>
    );
  };

  const actionBodyTemplate = (rowData: Notice) => (
    <div style={{ display: 'flex', gap: '6px' }}>
      {/* View */}
      <button
        onClick={() => { setSelectedNoticeId(rowData.notice_id); setShowDetailsModal(true); }}
        title="View Details"
        style={{
          width: '32px', height: '32px',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#9ca3af',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(14,34,93,0.08)';
          e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
          e.currentTarget.style.color = '#0e225d';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: '0.85rem' }}></i>
      </button>

      {/* Edit */}
      <button
        onClick={() => { setEditingNotice(rowData); setShowAddModal(true); }}
        title="Edit Notice"
        style={{
          width: '32px', height: '32px',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#9ca3af',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(251,178,31,0.12)';
          e.currentTarget.style.borderColor = 'rgba(251,178,31,0.35)';
          e.currentTarget.style.color = '#b8860b';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-pencil" style={{ fontSize: '0.85rem' }}></i>
      </button>

      {/* Delete */}
      <button
        onClick={() => handleDeleteNotice(rowData.notice_id)}
        title="Delete Notice"
        style={{
          width: '32px', height: '32px',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#9ca3af',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(239,68,68,0.1)';
          e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)';
          e.currentTarget.style.color = '#c62828';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-trash" style={{ fontSize: '0.85rem' }}></i>
      </button>
    </div>
  );

  const renderPaginationNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    const totalPages = pagination.pages;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', page, '...', totalPages);
      }
    }

    return pages.map((p, idx) =>
      p === '...' ? (
        <span key={`ellipsis-${idx}`} style={{ padding: '8px 6px', color: '#9ca3af', fontSize: '0.85rem' }}>
          …
        </span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{
            minWidth: '36px', height: '36px',
            padding: '0 10px',
            background: page === p
              ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)'
              : 'transparent',
            color: page === p ? '#fbb21f' : '#0e225d',
            border: page === p ? 'none' : '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: page === p ? '700' : '500',
            cursor: 'pointer',
            boxShadow: page === p ? '0 2px 8px rgba(14,34,93,0.25)' : 'none',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (page !== p) {
              e.currentTarget.style.background = 'rgba(14,34,93,0.06)';
              e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (page !== p) {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }
          }}
        >
          {p}
        </button>
      )
    );
  };

  // ── Shared column header style ────────────────────────────────────────────
  const colHeaderStyle: React.CSSProperties = {
    background: '#fafafa',
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E5E7EB',
    padding: '14px 16px',
    letterSpacing: '0.6px'
  };

  const colBodyStyle: React.CSSProperties = {
    padding: '16px 16px',
    borderBottom: '1px solid #f3f4f6'
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (profileLoading || (loading && notices.length === 0)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner
            style={{ width: '48px', height: '48px' }}
            strokeWidth="3"
            animationDuration=".8s"
          />
          <p style={{ marginTop: '16px', color: '#0e225d', fontSize: '0.95rem', fontWeight: '500' }}>
            Loading notices...
          </p>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .nb-datatable .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: #9ca3af !important;
          font-size: 0.72rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid #E5E7EB !important;
          padding: 14px 16px !important;
        }
        .nb-datatable .p-datatable-tbody > tr {
          transition: background 0.15s ease;
        }
        .nb-datatable .p-datatable-tbody > tr:hover {
          background: rgba(14, 34, 93, 0.03) !important;
        }
        .nb-datatable .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 14px 16px !important;
        }
        .nb-datatable .p-checkbox .p-checkbox-box.p-highlight {
          background: #0e225d !important;
          border-color: #0e225d !important;
        }
        .nb-datatable .p-checkbox .p-checkbox-box:hover {
          border-color: #fbb21f !important;
        }
      `}</style>

      <div style={{ padding: '0', margin: '0 auto' }}>

        {/* ── Hero Header ───────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '2px 0 12px rgba(14, 34, 93, 0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          {/* Decorative orbs */}
          <div style={{
            position: 'absolute', top: '-60px', right: '-60px',
            width: '220px', height: '220px',
            background: 'rgba(251, 178, 31, 0.08)',
            borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute', bottom: '-40px', left: '30%',
            width: '140px', height: '140px',
            background: 'rgba(251, 178, 31, 0.05)',
            borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none'
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Eyebrow badge — gold accent */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              background: 'rgba(251, 178, 31, 0.18)',
              border: '1px solid rgba(251, 178, 31, 0.35)',
              borderRadius: '20px',
              marginBottom: '12px',
              fontSize: '0.68rem',
              fontWeight: '700',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: '#fbb21f'
            }}>
              <i className="pi pi-megaphone" style={{ fontSize: '0.7rem' }}></i>
              Communication Hub
            </div>

            <h1 style={{
              margin: '0 0 8px 0',
              fontSize: '1.75rem',
              fontWeight: '700',
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              Notice Board
            </h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
              {pagination.total} total notice{pagination.total !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Create button — gold filled */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <button
              onClick={() => { setEditingNotice(null); setShowAddModal(true); }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: '#fbb21f',
                border: 'none',
                borderRadius: '12px',
                color: '#0e225d',
                fontSize: '0.88rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(251, 178, 31, 0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                letterSpacing: '0.3px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(251, 178, 31, 0.5)';
                e.currentTarget.style.background = '#ffc93c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 178, 31, 0.4)';
                e.currentTarget.style.background = '#fbb21f';
              }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.9rem' }}></i>
              CREATE NOTICE
            </button>
          </div>
        </div>

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {error && (
          <Message
            severity="error"
            text={error}
            style={{ marginBottom: '20px', width: '100%', fontSize: '0.9rem' }}
          />
        )}

        {/* ── Table Card ────────────────────────────────────────────────── */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(14, 34, 93, 0.06)',
          border: '1px solid #E5E7EB',
          overflow: 'hidden'
        }}>

          {/* Table Toolbar */}
          <div style={{
            padding: '18px 24px',
            borderBottom: '2px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Accent bar */}
              <div style={{
                width: '4px', height: '22px',
                background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)',
                borderRadius: '4px'
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '1rem',
                fontWeight: '700',
                color: '#0e225d'
              }}>
                All Notices
              </h2>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', color: '#9ca3af', fontWeight: '600' }}>Show</span>
              <GlobalDropdownField
                value={limit}
                onChange={(val) => { setLimit(val); setPage(1); }}
                options={limitOptions}
                placeholder="Show 10"
              />

              {/* Filter button */}
              <button
                title="Filters (Coming Soon)"
                style={{
                  width: '38px', height: '38px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  background: 'white',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#E8EEF7';
                  e.currentTarget.style.borderColor = '#fbb21f';
                  e.currentTarget.style.color = '#0e225d';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.color = '#9ca3af';
                }}
              >
                <i className="pi pi-filter" style={{ fontSize: '0.85rem' }}></i>
              </button>

              {/* Bulk delete */}
              {selectedNotices && selectedNotices.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px',
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: '10px',
                    color: '#c62828',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239,68,68,0.18)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(239,68,68,0.1)';
                  }}
                >
                  <i className="pi pi-trash" style={{ fontSize: '0.82rem' }}></i>
                  DELETE ({selectedNotices.length})
                </button>
              )}
            </div>
          </div>

          {/* DataTable */}
          <DataTable
            value={notices}
            selection={selectedNotices}
            onSelectionChange={(e: any) => setSelectedNotices(e.value)}
            dataKey="notice_id"
            loading={loading}
            emptyMessage="No notices found"
            className="nb-datatable"
            style={{ fontSize: '0.85rem' }}
          >
            <Column
              selectionMode="multiple"
              headerStyle={{ ...colHeaderStyle, width: '3rem' }}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="notice_title"
              header="Notice Title"
              headerStyle={colHeaderStyle}
              bodyStyle={{ ...colBodyStyle, color: '#0e225d', fontWeight: '600', fontSize: '0.88rem' }}
            />
            <Column
              field="notice_body"
              header="Notice Body"
              body={noticeBodyTemplate}
              headerStyle={colHeaderStyle}
              bodyStyle={colBodyStyle}
            />
            <Column
              field="broadcast_type"
              header="Type"
              body={broadcastTypeTemplate}
              headerStyle={{ ...colHeaderStyle, textAlign: 'center' }}
              bodyStyle={{ ...colBodyStyle, textAlign: 'center' }}
            />
            <Column
              field="created_at"
              header="Created At"
              body={(rowData) => (
                <span style={{ color: '#6b7280', fontSize: '0.83rem' }}>
                  {formatDateTime(rowData.created_at)}
                </span>
              )}
              headerStyle={colHeaderStyle}
              bodyStyle={colBodyStyle}
            />
            <Column
              header="Actions"
              body={actionBodyTemplate}
              headerStyle={{ ...colHeaderStyle, width: '8rem', textAlign: 'center' }}
              bodyStyle={{ ...colBodyStyle, textAlign: 'center' }}
            />
          </DataTable>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div style={{
              padding: '18px 24px',
              borderTop: '2px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              {/* Info */}
              <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total}
              </span>

              {/* Page buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    width: '36px', height: '36px',
                    background: 'transparent',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    color: '#0e225d'
                  }}
                  onMouseEnter={(e) => { if (page !== 1) e.currentTarget.style.background = 'rgba(14,34,93,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className="pi pi-chevron-left" style={{ fontSize: '0.75rem' }}></i>
                </button>

                {renderPaginationNumbers()}

                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  style={{
                    width: '36px', height: '36px',
                    background: 'transparent',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                    opacity: page === pagination.pages ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    color: '#0e225d'
                  }}
                  onMouseEnter={(e) => { if (page !== pagination.pages) e.currentTarget.style.background = 'rgba(14,34,93,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className="pi pi-chevron-right" style={{ fontSize: '0.75rem' }}></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      <NoticeDetailsModal
        visible={showDetailsModal}
        noticeId={selectedNoticeId}
        onHide={() => setShowDetailsModal(false)}
      />

      {profile?.institute_id && profile?.branch_id && (
        <AddNoticeModal
          visible={showAddModal}
          instituteId={profile.institute_id}
          branchId={profile.branch_id}
          editingNotice={editingNotice}
          onHide={() => { setShowAddModal(false); setEditingNotice(null); }}
          onSuccess={() => { fetchNotices(); }}
        />
      )}
    </>
  );
};

export default NoticeBoard;